import {
  AccountBalanceOutlined,
  Apartment,
  CalculateOutlined,
  CalendarMonth,
  HandshakeOutlined,
  LocalAtm,
  MailOutlined,
  PersonAdd,
  PriceCheck,
  RequestPageOutlined,
  Settings,
  StickyNote2,
  SupervisedUserCircle,
  SyncAlt,
  SyncAltOutlined,
  TrendingUp,
  Workspaces,
  PaymentsOutlined,
  CheckCircle,
} from '@mui/icons-material';
import { FC } from 'react';
import { Redirect, Switch } from 'react-router-dom';

import * as paths from 'adminConstants/routes';
import Vehicles from 'routes/LpPages/Vehicle/ListVehicles';
import CommonRoutes from './CommonRoutes';
import RestrictedRoute from './RestrictedRoute';

import BankReconciliation from 'routes/LpPages/BankReconciliation';
import AddEditCapitalAction from 'routes/LpPages/CapitalActions/AddEditCapitalAction';
import CapitalActionSuccess from 'routes/LpPages/CapitalActions/AddEditCapitalAction/Success';
import CreateCapitalActionNotice from 'routes/LpPages/CapitalActions/CreateNotice';
import ListCapitalActions from 'routes/LpPages/CapitalActions/ListCapitalActions';
import ListCommitments from 'routes/LpPages/Commitments/ListCommitments';
import ViewCommitment from 'routes/LpPages/Commitments/ViewCommitment';
import AddEditCompany from 'routes/LpPages/Company/AddEditCompany';
import ListCompanies from 'routes/LpPages/Company/ListCompanies';
import Equalisations from 'routes/LpPages/Equalisations';
import AddEqualisation from 'routes/LpPages/Equalisations/AddEqualisation';
import EqualisationsEditor from 'routes/LpPages/Equalisations/Editor';
import ProcessCloseSuccess from 'routes/LpPages/Equalisations/ProcessCloseSuccess';
import FeeManager from 'routes/LpPages/Fees/FeeManager';
import FirmSettings from 'routes/LpPages/FirmSettings';
import FundAccounting from 'routes/LpPages/FundAccounting';
import FundSetup from 'routes/LpPages/FundSetup';
import AddEditIncomeOrExpense from 'routes/LpPages/IncomeAndExpenses/AddEditIncomeOrExpense';
import IncomeOrExpenseSuccess from 'routes/LpPages/IncomeAndExpenses/AddEditIncomeOrExpense/Success';
import ListIncomeOrExpense from 'routes/LpPages/IncomeAndExpenses/ListIncomeAndExpenses';
import ViewIncomeOrExpense from 'routes/LpPages/IncomeAndExpenses/ViewIncomeOrExpense';
import CreateInvestment from 'routes/LpPages/Investment/CreateInvestment';
import InvestmentSuccess from 'routes/LpPages/Investment/CreateInvestment/InvestmentSuccess';
import ListInvestments from 'routes/LpPages/Investment/ListInvestments';
import ViewInvestment from 'routes/LpPages/Investment/ViewInvestment';
import AddEditManualAction from 'routes/LpPages/ManualJournal/AddEditManualAction';
import ListManualActions from 'routes/LpPages/ManualJournal/ListManualActions';
import Onboarding from 'routes/LpPages/Onboarding';
import AddPartnerTransfer from 'routes/LpPages/PartnerTransfers/CreatePartnerTransfer';
import ListPartnerTransfers from 'routes/LpPages/PartnerTransfers/ListPartnerTransfers';
import PeriodEndReview from 'routes/LpPages/PeriodEndReview';
import PeriodEndReviewDetail from 'routes/LpPages/PeriodEndReview/Detail';
import InvestorUpdates from 'routes/LpPages/InvestorUpdates';
import PeriodEndReport from 'routes/LpPages/InvestorUpdates/PeriodEndReport';
import AddEditPeriodEndReport from 'routes/LpPages/InvestorUpdates/AddEditPeriodEndReport';
import UserManagement from 'routes/LpPages/UserManagement';
import AddVehicle from 'routes/LpPages/Vehicle/AddVehicle';
import ProfilePage from 'routes/Pages/Profile/LpProfile';
import Revaluations from '../LpPages/Company/Revaluations';
import { CreateReversal, ViewReversal } from '../LpPages/Reversal';
import Realisation from '../LpPages/Realisation/CreateRealisation';
import ListRealisations from '../LpPages/Realisation/ListRealisations';
import ViewRealisation from '../LpPages/Realisation/ViewRealisation';

const LpRoutes: FC = () => (
  <Switch>
    <Redirect path="/" exact to={paths.LP_ONBOARDING} />
    <RestrictedRoute
      component={Vehicles}
      path={paths.VEHICLES}
      breadcrumbs={['Vehicles']}
      icon={Workspaces}
      useErrorBoundary
    />
    <RestrictedRoute
      component={AddVehicle}
      path={paths.VEHICLE_ADD}
      breadcrumbs={[{ label: 'Vehicles', link: paths.VEHICLES }, 'Add Vehicle']}
      useErrorBoundary
    />
    <RestrictedRoute
      component={FundAccounting}
      path={paths.ACCOUNTING}
      breadcrumbs={['Accounting']}
      icon={CalculateOutlined}
      useErrorBoundary
    />
    <RestrictedRoute
      component={FundSetup}
      path={paths.FUND_SETUP}
      breadcrumbs={['Fund Setup']}
      icon={PriceCheck}
      useErrorBoundary
    />
    <RestrictedRoute
      component={BankReconciliation}
      path={paths.BANK_RECONCILIATION}
      icon={AccountBalanceOutlined}
      breadcrumbs={['Bank reconciliation']}
      useErrorBoundary
    />
    <RestrictedRoute
      component={AddEditIncomeOrExpense}
      path={paths.INCOME_OR_EXPENSE_ADD}
      breadcrumbs={[
        { label: 'Income and expenses', link: paths.INCOME_OR_EXPENSE_LIST },
        'Add income or expense',
      ]}
      icon={SyncAlt}
      useErrorBoundary
    />
    <RestrictedRoute
      component={IncomeOrExpenseSuccess}
      path={paths.INCOME_OR_EXPENSE_SUCCESS}
      breadcrumbs={['Income and expenses']}
      icon={SyncAlt}
      useErrorBoundary
    />
    <RestrictedRoute
      component={AddEditIncomeOrExpense}
      path={paths.INCOME_OR_EXPENSE_EDIT}
      breadcrumbs={[
        { label: 'Income and expenses', link: paths.INCOME_OR_EXPENSE_LIST },
        'Edit income or expense',
      ]}
      icon={SyncAlt}
      useErrorBoundary
    />
    <RestrictedRoute
      component={ListIncomeOrExpense}
      path={paths.INCOME_OR_EXPENSE_LIST}
      breadcrumbs={['Income and expenses']}
      icon={SyncAlt}
      useErrorBoundary
    />
    <RestrictedRoute
      component={ViewIncomeOrExpense}
      path={paths.INCOME_OR_EXPENSE_VIEW}
      breadcrumbs={[
        { label: 'Income and expenses', link: paths.INCOME_OR_EXPENSE_LIST },
        'View income or expense',
      ]}
      icon={SyncAlt}
      useErrorBoundary
    />
    <RestrictedRoute
      component={CreateReversal}
      path={paths.REVERSAL_CREATE}
      breadcrumbs={[
        { label: 'Income and expenses', link: paths.INCOME_OR_EXPENSE_LIST },
        'Reverse action',
      ]}
      icon={SyncAlt}
      useErrorBoundary
    />
    <RestrictedRoute
      component={ViewReversal}
      path={paths.REVERSAL_VIEW}
      breadcrumbs={[
        { label: 'Income and expenses', link: paths.INCOME_OR_EXPENSE_LIST },
        'View reversal',
      ]}
      icon={SyncAlt}
      useErrorBoundary
    />
    <RestrictedRoute
      component={CreateInvestment}
      path={paths.LP_INVESTMENT_CREATE}
      breadcrumbs={[
        { label: 'Investments', link: paths.LP_INVESTMENT_LIST },
        'Create investment',
      ]}
      icon={TrendingUp}
      useErrorBoundary
    />
    <RestrictedRoute
      component={CreateInvestment}
      path={paths.LP_INVESTMENT_EDIT}
      breadcrumbs={[
        { label: 'Investments', link: paths.LP_INVESTMENT_LIST },
        'Edit investment',
      ]}
      icon={TrendingUp}
      useErrorBoundary
    />
    <RestrictedRoute
      component={ViewInvestment}
      path={paths.LP_INVESTMENT_VIEW}
      breadcrumbs={[
        { label: 'Investments', link: paths.LP_INVESTMENT_LIST },
        'View investment',
      ]}
      icon={TrendingUp}
      useErrorBoundary
    />
    <RestrictedRoute
      component={InvestmentSuccess}
      path={paths.LP_INVESTMENT_SUCCESS}
      breadcrumbs={[
        { label: 'Investments', link: paths.LP_INVESTMENT_LIST },
        'Success',
      ]}
      icon={TrendingUp}
      useErrorBoundary
    />
    <RestrictedRoute
      component={ListInvestments}
      path={paths.LP_INVESTMENT_LIST}
      breadcrumbs={['Investments']}
      icon={TrendingUp}
      useErrorBoundary
    />
    <RestrictedRoute
      component={ListManualActions}
      path={paths.LP_MANUAL_ACTION_LIST}
      breadcrumbs={['Manual Journal']}
      icon={StickyNote2}
      useErrorBoundary
    />
    <RestrictedRoute
      component={AddEditManualAction}
      path={paths.LP_MANUAL_ACTION_CREATE}
      breadcrumbs={[
        { label: 'Manual Journal', link: paths.LP_MANUAL_ACTION_LIST },
        'Add Manual journal',
      ]}
      icon={StickyNote2}
      useErrorBoundary
    />
    <RestrictedRoute
      component={AddEditManualAction}
      path={paths.LP_MANUAL_ACTION_EDIT}
      breadcrumbs={[
        { label: 'Manual Journal', link: paths.LP_MANUAL_ACTION_LIST },
        'Edit Manual journal',
      ]}
      icon={StickyNote2}
      useErrorBoundary
    />
    <RestrictedRoute
      key="company-add"
      component={AddEditCompany}
      path={paths.LP_COMPANY_ADD}
      icon={Apartment}
      breadcrumbs={[
        { label: 'Holdings', link: paths.LP_COMPANY_LIST },
        'Create holding',
      ]}
      useErrorBoundary
    />
    <RestrictedRoute
      key="company-edit"
      component={AddEditCompany}
      breadcrumbs={[
        { label: 'Holdings', link: paths.LP_COMPANY_LIST },
        'Edit holding',
      ]}
      icon={Apartment}
      path={paths.LP_COMPANY_EDIT}
      useErrorBoundary
    />
    <RestrictedRoute
      component={ListCompanies}
      path={paths.LP_COMPANY_LIST}
      icon={Apartment}
      breadcrumbs={['Holdings']}
      useErrorBoundary
    />
    <RestrictedRoute
      component={Revaluations}
      icon={Apartment}
      breadcrumbs={[
        { label: 'Companies', link: paths.LP_COMPANY_LIST },
        'Revalue company',
      ]}
      path={paths.LP_REVALUATIONS}
      useErrorBoundary
    />
    <RestrictedRoute
      component={Realisation}
      path={paths.LP_REALISATION_CREATE}
      breadcrumbs={[
        { label: 'Realisations', link: paths.LP_REALISATION_LIST },
        'Add new realisation',
      ]}
      icon={PaymentsOutlined}
      useErrorBoundary
    />
    <RestrictedRoute
      component={ListRealisations}
      path={paths.LP_REALISATION_LIST}
      breadcrumbs={['Realisations']}
      icon={PaymentsOutlined}
      useErrorBoundary
    />
    <RestrictedRoute
      component={ViewRealisation}
      path={paths.LP_REALISATION_VIEW}
      icon={PaymentsOutlined}
      breadcrumbs={[
        { label: 'Realisations', link: paths.LP_REALISATION_LIST },
        'View realisation',
      ]}
      useErrorBoundary
    />
    <RestrictedRoute
      component={ListCapitalActions}
      path={paths.LP_CAPITAL_ACTION_LIST}
      breadcrumbs={['Capital actions']}
      icon={LocalAtm}
      useErrorBoundary
    />
    <RestrictedRoute
      component={AddEditCapitalAction}
      path={paths.LP_CAPITAL_ACTION_CREATE}
      useErrorBoundary
      breadcrumbs={[
        { label: 'Capital actions', link: paths.LP_CAPITAL_ACTION_LIST },
        'Create Capital action',
      ]}
      icon={LocalAtm}
    />
    <RestrictedRoute
      component={CapitalActionSuccess}
      path={paths.LP_CAPITAL_ACTION_CREATE_SUCCESS}
      useErrorBoundary
      breadcrumbs={[
        { label: 'Capital actions', link: paths.LP_CAPITAL_ACTION_LIST },
        'Capital action added',
      ]}
      icon={LocalAtm}
    />
    <RestrictedRoute
      component={AddEditCapitalAction}
      path={paths.LP_CAPITAL_ACTION_EDIT}
      useErrorBoundary
      breadcrumbs={[
        { label: 'Capital actions', link: paths.LP_CAPITAL_ACTION_LIST },
        'Edit capital action',
      ]}
      icon={LocalAtm}
    />
    <RestrictedRoute
      component={CreateCapitalActionNotice}
      path={paths.LP_CAPITAL_ACTION_NOTICE_CREATE}
      breadcrumbs={[
        { label: 'Capital actions', link: paths.LP_CAPITAL_ACTION_LIST },
        'Capital action notice',
      ]}
      icon={LocalAtm}
      useErrorBoundary
    />
    <RestrictedRoute
      component={ListCommitments}
      path={paths.COMMITMENTS}
      breadcrumbs={['Commitments']}
      icon={CheckCircle}
    />
    <RestrictedRoute
      component={ViewCommitment}
      path={paths.COMMITMENTS_VIEW}
      breadcrumbs={[{ label: 'Commitments', link: paths.COMMITMENTS }]}
      icon={CheckCircle}
    />
    <RestrictedRoute
      icon={RequestPageOutlined}
      breadcrumbs={['Fee Manager']}
      component={FeeManager}
      path={paths.FEE_MANAGER}
    />
    <RestrictedRoute
      component={ProcessCloseSuccess}
      path={paths.EQUALISATIONS_PROCESS_CLOSE_SUCCESS}
      breadcrumbs={[
        { label: 'Close and equalise', link: paths.EQUALISATIONS },
        'Add Close',
      ]}
      icon={SyncAltOutlined}
    />
    <RestrictedRoute
      component={EqualisationsEditor}
      path={paths.EQUALISATIONS_EDITOR}
      breadcrumbs={[
        { label: 'Close and equalise', link: paths.EQUALISATIONS },
        'Add Close',
      ]}
      icon={SyncAltOutlined}
      useErrorBoundary
    />
    <RestrictedRoute
      component={Equalisations}
      path={paths.EQUALISATIONS}
      breadcrumbs={['Close and equalise']}
      icon={SyncAltOutlined}
      useErrorBoundary
    />
    <RestrictedRoute
      component={AddEqualisation}
      path={paths.EQUALISATIONS_ADD}
      icon={SyncAltOutlined}
      breadcrumbs={[
        { label: 'Close and equalise', link: paths.EQUALISATIONS },
        'Add Close',
      ]}
      useErrorBoundary
    />
    <RestrictedRoute
      component={InvestorUpdates}
      path={paths.LP_INVESTOR_UPDATES}
      exact
      useErrorBoundary
      breadcrumbs={['Investor Updates']}
      icon={MailOutlined}
    />
    <RestrictedRoute
      component={PeriodEndReport}
      path={paths.PERIOD_END_REPORT_LIST}
      exact
      useErrorBoundary
      breadcrumbs={[
        {
          label: 'Investor Updates',
          link: paths.LP_INVESTOR_UPDATES,
        },
        'Period-end report',
      ]}
      icon={MailOutlined}
    />
    <RestrictedRoute
      component={AddEditPeriodEndReport}
      path={paths.PERIOD_END_REPORT_CREATE}
      useErrorBoundary
      breadcrumbs={[
        {
          label: 'Investor Updates',
          link: paths.LP_INVESTOR_UPDATES,
        },
        {
          label: 'Period-end report',
          link: paths.PERIOD_END_REPORT_LIST,
        },
        'Create',
      ]}
      icon={MailOutlined}
    />
    <RestrictedRoute
      component={AddEditPeriodEndReport}
      path={paths.PERIOD_END_REPORT_EDIT}
      useErrorBoundary
      breadcrumbs={[
        {
          label: 'Investor Updates',
          link: paths.LP_INVESTOR_UPDATES,
        },
        {
          label: 'Period-end report',
          link: paths.PERIOD_END_REPORT_LIST,
        },
        'Edit',
      ]}
      icon={MailOutlined}
    />
    <RestrictedRoute
      component={PeriodEndReviewDetail}
      path={paths.LP_PERIOD_END_REVIEW_DETAIL}
      useErrorBoundary
      breadcrumbs={[
        { label: 'Period-End Review', link: paths.LP_PERIOD_END_REVIEW },
      ]}
      icon={CalendarMonth}
    />
    <RestrictedRoute
      component={PeriodEndReview}
      path={paths.LP_PERIOD_END_REVIEW}
      useErrorBoundary
      breadcrumbs={['Period-End Review']}
      icon={CalendarMonth}
    />
    <RestrictedRoute
      component={Onboarding}
      path={paths.LP_ONBOARDING}
      useErrorBoundary
      breadcrumbs={['Onboarding']}
      icon={PersonAdd}
    />
    <RestrictedRoute
      component={ListPartnerTransfers}
      path={paths.PARTNER_TRANSFERS_LIST}
      exact
      icon={HandshakeOutlined}
      useErrorBoundary
      breadcrumbs={['Partner Transfers']}
    />
    <RestrictedRoute
      component={AddPartnerTransfer}
      path={paths.PARTNER_TRANSFERS_CREATE}
      useErrorBoundary
      icon={HandshakeOutlined}
      breadcrumbs={[
        { label: 'Partner Transfers', link: paths.PARTNER_TRANSFERS_LIST },
        'Partner transfer',
      ]}
    />
    <RestrictedRoute
      component={AddPartnerTransfer}
      path={paths.PARTNER_TRANSFERS_EDIT}
      icon={HandshakeOutlined}
      useErrorBoundary
      breadcrumbs={[
        { label: 'Partner Transfers', link: paths.PARTNER_TRANSFERS_LIST },
        'Partner transfer',
      ]}
    />
    <RestrictedRoute component={ProfilePage} path={paths.LP_PROFILE} />
    <RestrictedRoute
      component={FirmSettings}
      path={paths.LP_FIRM_SETTINGS}
      breadcrumbs={['Firm settings']}
      icon={Settings}
      useErrorBoundary
    />
    <RestrictedRoute
      component={UserManagement}
      path={paths.USER_MANAGEMENT}
      breadcrumbs={['User management']}
      icon={SupervisedUserCircle}
      useErrorBoundary
    />
    {...CommonRoutes}
  </Switch>
);

export default LpRoutes;
